/**
 * Search-result grouping for the SearchBox (spec §7): hits arrive flat and
 * scored; the UI shows them grouped by kind in a fixed order, with one flat
 * index for keyboard navigation across groups.
 */
import type { SearchHit, SearchHitKind } from "@/api/types";

export const HIT_KIND_ORDER: readonly SearchHitKind[] = [
  "family",
  "address",
  "tx",
  "ledger",
  "hook",
  "note",
  "view",
];

export const HIT_KIND_LABEL: Record<SearchHitKind, string> = {
  family: "families",
  address: "addresses",
  tx: "transactions",
  ledger: "ledgers",
  hook: "hooks",
  note: "notes",
  view: "views",
};

export interface HitGroup {
  kind: SearchHitKind;
  label: string;
  /** hits of this kind, best score first (stable on ties) */
  hits: SearchHit[];
  /** flat index of the first hit of this group */
  offset: number;
}

/** Group hits by kind in HIT_KIND_ORDER; each group sorted by score desc. */
export function groupHits(hits: readonly SearchHit[]): HitGroup[] {
  const byKind = new Map<SearchHitKind, SearchHit[]>();
  for (const h of hits) {
    const list = byKind.get(h.kind);
    if (list) list.push(h);
    else byKind.set(h.kind, [h]);
  }
  const groups: HitGroup[] = [];
  let offset = 0;
  for (const kind of HIT_KIND_ORDER) {
    const list = byKind.get(kind);
    if (!list || list.length === 0) continue;
    const sorted = list
      .map((h, i) => ({ h, i }))
      .sort((a, b) => b.h.score - a.h.score || a.i - b.i)
      .map((x) => x.h);
    groups.push({ kind, label: HIT_KIND_LABEL[kind], hits: sorted, offset });
    offset += sorted.length;
  }
  return groups;
}

/** The flat list the keyboard walks (same order as the groups). */
export function flattenGroups(groups: readonly HitGroup[]): SearchHit[] {
  return groups.flatMap((g) => g.hits);
}

/** Wrap-around move of the active index. */
export function stepIndex(active: number, delta: number, count: number): number {
  if (count <= 0) return 0;
  return (((active + delta) % count) + count) % count;
}

/** "token:base:0xabc…" -> "base · 0xabc…"; other job ids as "liquidity · chain". */
export function jobLabel(job: string | undefined, symbols: Record<string, string> = {}): string | undefined {
  if (!job) return undefined;
  const sym = symbols[job];
  const parts = job.split(":");
  if (parts[0] === "token" && parts.length === 3) {
    const token = parts[2] ?? "";
    return `${sym ?? `${token.slice(0, 6)}…${token.slice(-4)}`} · ${parts[1]}`;
  }
  if (parts.length === 2) return `${parts[0]} · ${parts[1]}`;
  return job;
}
